import WRAP from "WRAP-JS";
import { _groupBy, _orderBy, formatUTCDate, maxBy, minBy, parseUTCDate } from "../../../utils/FPEUtils";
import LegendVaa from "../legend/LegendVaa";
import { configVaaAddingLayer, configVaaDh, configVaaLayer } from "./config/ConfigVaa";
import { WX_ID_VAA } from "../../../store/WeatherContentsState";

const THRESHOLD = 24 * 3600 * 1000;
const SCHEDULED_REPORT_INTERVAL = 6 * 3600 * 1000;
const STOP_MESSAGES = [
  "NXT ADVISORY: NO FURTHER ADVISORY",
  "NXT ADVISORY: NO FURTHER ADVISORIE",
  "NXT ADVISORY: NO FURTHER ADVISORIES",
  "NXT ADVISORY: NO FURTHER ADVISORIS",
];
class Vaa {
  static NAME = WX_ID_VAA;

  dh;
  layer;
  ready = false;
  layerConfig;
  addingLayerConfig;
  altitude;
  baseTime = '---';
  dispatcher;
  visible;

  constructor(altitude) {
    this.altitude = altitude;
    this.layer = new WRAP.Geo.Layer(Vaa.NAME);
    this.layer.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configVaaDh);

      this.dh.inspect((ref) => {
        this._filterWRAPData(ref);
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
        resolve();
      })
    });
  }

  initLayer() {
    this.layerConfig = configVaaLayer;
    this.addingLayerConfig = configVaaAddingLayer;

    this.layer.configure(this.dh, configVaaLayer);
    this.layer.updated(this._addingDrawLayer.bind(this));
    this.layer.setTooltip(this._toolTip.bind(this));
    this.layer.setAttributes({
      filter: this._visibleFilter.bind(this)
    });
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (this.layer.visible() === visible) {
      return;
    }
    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }
    this.dh.setAutoUpdate('data', visible);
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getLegend() {
    return {
      id: Vaa.NAME,
      label: 'VAA',
      component: LegendVaa,
      contents: this
    };
  }

  getTimeInformation() {
    return {
      id: Vaa.NAME,
      label: 'VAA',
      baseTime: this.baseTime
    }
  }

  applyAltitude(altitude) {
    this.altitude = altitude;
    this.layer.invalidate();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  changeRoutePointEditing(routePointEditing) {
    this.layer.disableInteraction(routePointEditing);
  }

  _filterWRAPData(ref) {
    let data = ref.data.retained.data;
    //TODO データ取得エラー時の挙動
    if (!data) return;
    const now = Date.now();

    // 電文単位に整形
    const tmp1 = data.features.reduce((accumulator, feature) => {
      if(feature.geometry && feature.geometry.type === 'Point') {
        accumulator.push({
          "smithonian_number": feature.smithonian_number,
          "announced_date": feature.announced_date,
          "vaac": feature.properties.vaac,
          "telegram": []
        });
      }
      const last = accumulator.slice(-1)[0];
      last.telegram.push(feature);
      return accumulator;
    },[]);

    // 24時間以内のデータでフィルタ
    const tmp2 = tmp1.filter(o => {
      return now - parseUTCDate(o.announced_date).getTime() <= THRESHOLD;
    });

    //smithonian_number毎のlatestTimeを求める
    const latestTimes = {};
    const _group = _groupBy(tmp2, 'smithonian_number');
    Object.entries(_group).reduce((accumulator, [key, value]) => {
      const max = value.reduce((a, b) => {
        return a.announced_date > b.announced_date ? a : b;
      });
      accumulator[key] = parseUTCDate(max.announced_date).getTime();
      return accumulator;
    }, latestTimes);

    //smithonian_number毎のlatestTimeから6時間以内のデータフィルタ
    const tmp3 = tmp2.filter(o => {
      return latestTimes[o.smithonian_number] - parseUTCDate(o.announced_date).getTime() < SCHEDULED_REPORT_INTERVAL;
    });

    //火山アイコンツールチップ用変数
    const vaacLatestTelegram = {}

    const tmp4 = _groupBy(tmp3, 'smithonian_number');
    const tmp5 = Object.entries(tmp4).reduce((accumulator, [key, sameSmithonian]) => {
      const vaacGroup = _groupBy(sameSmithonian, 'vaac');
      for (const [vaac, sameVaac] of Object.entries(vaacGroup)) {
        const orderByAnnounced = _orderBy(sameVaac, 'announced_date', 'desc');
        // 最新のみを表示
        orderByAnnounced.splice(1);
        const latestAnnounced = orderByAnnounced[0];
        let vaaName;
        latestAnnounced.telegram.forEach(feature => {
          if (feature.geometry && feature.geometry.type === 'Point') {
            vaaName = feature.properties.name;
            (vaacLatestTelegram[feature.smithonian_number] || (vaacLatestTelegram[feature.smithonian_number] = [])).push(feature.properties);
          }
          let tau = "";
          if (feature.properties.anlsis_layer_flag === '1') {
            tau = "act";
          } else if (feature.properties.fcst_tau === '6') {
            tau = "6h";
          } else if (feature.properties.fcst_tau === '12') {
            tau = "12h";
          } else if (feature.properties.fcst_tau === '18') {
            tau = "18h";
          }
          if (tau) {
            feature.properties.color_type = "latest_" + tau;
            //polygonにはvaacとnameがないので補填
            feature.properties.vaac = vaac;
            feature.properties.name = vaaName;
          }
        });
        if (STOP_MESSAGES.some(word => {
          return latestAnnounced.telegram[0].properties.bulletin.indexOf(word) !== -1
        })) {
          // 1つでもPolygonがある場合は削除しない
          if(!latestAnnounced.telegram.some(telegram=>{
            return telegram.geometry && telegram.geometry.type === 'Polygon';
          })) {
            // 拡散予測(Polygon)は表示しない、火山アイコン(Point)は表示する
            latestAnnounced.telegram.splice(1);
          }
        }
        accumulator.push(latestAnnounced);
      }
      return accumulator;
    }, []);

    const tmp6 = tmp5.flatMap(features => {
      //telegram配列を分解してフラットにする。
      return features.telegram;
    });

    const tmp7 = tmp6.filter(feature => {
      //火山アイコンはLatestのみ表示。
      if(feature.geometry && feature.geometry.type === 'Point') {
        const ret = latestTimes[feature.smithonian_number] === parseUTCDate(feature.announced_date).getTime();
        if (ret) {
          feature.data = vaacLatestTelegram[feature.smithonian_number];
        }
        return ret;
      }
      return true;
    });

    data.features = _orderBy(tmp7, [(feature) => {
      if(feature.properties.anlsis_layer_flag === '1') {
        return 4;
      } else if(feature.properties.fcst_tau === '6') {
        return 3;
      } else if(feature.properties.fcst_tau === '12') {
        return 2;
      } else if(feature.properties.fcst_tau === '18') {
        return 1;
      } else if(feature.geometry && feature.geometry.type === 'Point') {
        return 0;
      } else {
        feature.properties.display_flag = false;
        return -1;
      }
    }, 'announced_date']);

    if(data.features.length > 0) {
      const maxTime = data.features.reduce((a, b) => {
        const t = b.announced_date;
        return a > t ? a : t;
      }, 0);
      this.baseTime = formatUTCDate(parseUTCDate(maxTime), '%Y/%m/%d %H:%MZ');
    } else {
      this.basetime = 'No Data'
    }

  }
  _visibleFilter(feature) {
    //高度で噴煙をフィルタリング
    if (feature.geometry && feature.geometry.type !== 'Polygon') {
      return true;
    }

    const fl1 = feature.properties.layer_height_high;
    const fl2 = feature.properties.layer_height_low;

    // 高度情報が共に存在しなければフィルタリングはしない
    if (!fl1 && !fl2) {
      return true;
    }

    const low = fl2 ? fl2 / 100 : -1;
    const high = fl1 ? fl1 / 100 : -1;
    if (high !== -1 && low === -1 && this.altitude[0] > high) {
      return false;
    } else if (high === -1 && low !== -1 && this.altitude[1] < low) {
      return false;
    } else if (high !== -1 && low !== -1) {
      if (this.altitude[0] > high || this.altitude[1] < low) {
        return false;
      }
    }
    return true;
  }

  _addingDrawLayer() {
    const data = this.dh.retained.data.features;
    const layer = this.layer;
    const config = this.addingLayerConfig['default'];
    const smithonianGroup = _groupBy(data, 'smithonian_number');

    const toInt = v => { return v && parseInt(v); };

    for (const features of Object.values(smithonianGroup)) {
      const polygons = features.reduce((accumulator, feature) => {
        if (feature.geometry && feature.geometry.type === 'Polygon') {
          if (this._visibleFilter(feature)) {
            accumulator.push(feature);
          }
        }
        return accumulator;
      }, []);

      //smithonianの中でlatestなannounced_dateを持つPoint
      const p = features.reduce((accumulator, feature) => {
        if (feature.geometry && feature.geometry.type === 'Point') {
          return accumulator.announced_date > feature.announced_date ? accumulator : feature;
        }
        return accumulator;
      }, {});

      let offsetY = config.offsetY;
      const point = p.geometry.coordinates;

      [
        ['anlsis_layer_flag', '1'],
        ['fcst_tau', '6'],
        ['fcst_tau', '12'],
        ['fcst_tau', '18'],
      ].forEach(cond => {
        const tau = polygons.filter(polygon => {
          return polygon.properties[cond[0]] === cond[1];
        });
        if(tau.length === 0) {
          return;
        }

        const low = minBy(tau, (o)=>{
          return toInt(o.properties.layer_height_low);});

        const high = maxBy(tau, (o) => {
          return toInt(o.properties.layer_height_high);
        });

        // 高度情報が共に存在しなければラベルは表示しない.
        if(!low && !high) return;

        // 片方だけ存在しない場合は、-- を表示.
        const text = getAltitude(low.properties.layer_height_low, high.properties.layer_height_high);

        // Style (文字色) 取得.
        const base = high || low;
        const arg = base.properties.color_type ? {
          selector: {
            key: 'color_type',
            value: base.properties.color_type.replace(/^[^_]+/, 'latest')
          }
        } : (feature) => {
          //TODO このコードが謎。そもそもcolor_typeが設定されないケースがあるのか？
          return !feature.selector;
        };
        const style = this.layerConfig.Attributes.features.find(feature => {
          return feature.selector.value === arg.selector.value;
        }).style.default;

        let fillStyle;
        if (style.text_color) {
          fillStyle = style.text_color;
        } else {
          fillStyle = style.fillStyle ? style.fillStyle : style.line_color;
        }

        // 噴煙拡散予想の高度ラベル表示.
        const label = new WRAP.Geo.Feature.Text(Object.assign({}, config.alt, {
          fillStyle: fillStyle,
          point: point,
          text: text,
          offsetY: offsetY
        }));
        label.data = tau;
        layer.addFeature(label);

        offsetY -= config.alt.fontSize;
      });
    }
  }

  _toolTip(geo)  {
    if (!geo) return;
    if (geo.geometry.type === 'Point') {
      const dataList = _orderBy(geo.data, "anlsis_date", "desc");
      const tooltip = ['<div style="text-align: left; max-width: 450px;">'];
      dataList.forEach((p, i) => {
        if (i === 0) {
          const name = geo.properties.name;
          tooltip.push(`Volcano : ${name}`);
        } else {
          tooltip.push('<hr/>');
        }
        const vaac = p.vaac;
        const announce = p.anlsis_date ? formatUTCDate(parseUTCDate(p.anlsis_date), '%Y/%m/%d %H:%MZ') : '---';
        const t = `<ul>
                  <li>VAAC : ${vaac}</li>
                  <li>Announce : ${announce}</li>
                  <li style="font-size: 11px">${p.bulletin}</li>
                </ul>`
        tooltip.push(t);
      })
      tooltip.push('</div>');
      return tooltip.join('');
    } else if (geo.geometry.type === 'Polygon') {
      const name = geo.properties.name;
      const vaac = geo.properties.vaac;
      const announce = formatUTCDate(parseUTCDate(geo.announced_date), '%Y/%m/%d %H:%MZ');
      let valid;
      if (geo.properties.anlsis_layer_flag === '1') {
        valid = geo.properties.anlsis_date ? formatUTCDate(parseUTCDate(geo.properties.anlsis_date), '%Y/%m/%d %H:%MZ') : '---';
      } else {
        valid = geo.properties.fcasd ? formatUTCDate(parseUTCDate(geo.properties.fcasd), '%Y/%m/%d %H:%MZ') : '---';
      }
      const altitude = getAltitude(geo.properties.layer_height_low, geo.properties.layer_height_high);
      return `<div style="text-align: left; max-width: 250px;">
                <ul>
                  <li>Volcano : ${name}</li>
                  <li>VAAC : ${vaac}</li>
                  <li>Announce : ${announce}</li>
                  <li>Valid: ${valid}</li> 
                  <li>${altitude}</li>
                </ul>       
              </div>`;
    }
  }

  _getSelectedFlightLevel() {
    const low = Math.min(...this.altitude);
    const high = Math.max(...this.altitude);
    let fl;
    if(low === 0) {
      fl = 'SFC';
    } else {
      fl = `FL${('000' + low).slice(-3)}`;
    }
    if(low !== high) {
      fl += ` - FL${('000' + high).slice(-3)}`;
    }
    return fl;
  }
}

const getAltitude = (low, high) => {
  const fl1 = high;
  const fl2 = low;

  if (!fl1 && !fl2) {
    return '';
  }

  const _low = fl2 ? fl2 / 100 : ' - ';
  const _high = fl1 ? fl1 / 100 : ' - ';

  if (_low === _high) {
    return 'FL' + _low;
  } else {
    if (_low === 0) {
      return 'SFC/FL' + _high;
    }
    return 'FL' + _low + '/' + _high;
  }
}

export default Vaa;